import React from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { X, User, BookOpen, Calendar, ArrowRight, Mail } from 'lucide-react';

export default function AuthorModal() {
  const {
    authorModalData,
    closeAuthorModal,
    users,
    articles,
    navigate,
    setSearchQuery,
    setSelectedCategory,
    settings,
  } = useApp();

  if (!authorModalData) return null;

  const authorName = authorModalData.name;
  const siteTitle = settings?.siteName || 'Nusantara Redaksi';
  const primaryColor = settings?.theme.primaryColor || '#059669';

  // Find matching user profile
  const authorUser = users?.find(
    (u) =>
      u.name.trim().toLowerCase() === authorName.trim().toLowerCase() ||
      u.username.trim().toLowerCase() === authorName.trim().toLowerCase() ||
      u.email.trim().toLowerCase() === authorName.trim().toLowerCase()
  );

  const avatar =
    authorUser?.avatar ||
    authorModalData.avatar ||
    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80';

  const bio =
    authorUser?.bio ||
    (settings?.articleAuthorBioNote
      ? settings.articleAuthorBioNote.replace(/\{author\}/g, authorName)
      : `${authorName} adalah Advokat / Praktisi Hukum di ${siteTitle}, berdedikasi memberikan pendampingan hukum, analisis yuridis presisi, serta edukasi hukum berintegritas bagi klien dan masyarakat.`);

  // Filter author's articles
  const authorArticles = articles.filter(
    (a) =>
      a.author.trim().toLowerCase() === authorName.trim().toLowerCase() &&
      a.status === 'published'
  );

  const handleSeeAllArticles = () => {
    setSearchQuery(authorName);
    closeAuthorModal();
    navigate('blog');
  };

  const handleArticleClick = (slug: string) => {
    closeAuthorModal();
    navigate('article-detail', slug);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative bg-slate-900 text-white p-6 pb-12 overflow-hidden">
          {/* Subtle background glow */}
          <div
            className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-2xl opacity-30"
            style={{ backgroundColor: primaryColor }}
          />

          <button
            type="button"
            onClick={(e) => {
              console.log('Close button clicked');
              closeAuthorModal();
            }}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer z-10 pointer-events-auto"
            title="Close Modal"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="relative z-10 flex items-center gap-2 text-sm text-white font-bold tracking-wide mb-1">
            <User className="w-4 h-4 text-emerald-400" />
            <span>Author Profile</span>
          </div>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="p-6 pt-0 -mt-10 overflow-y-auto space-y-6 flex-1">
          {/* Author Header Card */}
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-3">
              <img
                src={avatar}
                alt={authorName}
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-white shadow-xl"
              />
              <span className="absolute bottom-1 right-1 w-5 h-5 bg-emerald-500 border-2 border-white rounded-full flex items-center justify-center text-white text-[10px] shadow-xs" title="Verified Author">
                ✓
              </span>
            </div>

            <h3 className="text-xl font-black text-slate-900 leading-tight">
              {authorName}
            </h3>

            {authorUser?.email && (
              <a
                href={`mailto:${authorUser.email}`}
                className="mt-2 text-xs text-slate-500 hover:text-emerald-700 font-medium flex items-center gap-1 transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                <span>{authorUser.email}</span>
              </a>
            )}
          </div>

          {/* Description / Bio Box */}
          <div className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-4 sm:p-5 text-left">
            <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-slate-500" />
              <span>Author Bio</span>
            </h4>
            <p className="text-xs text-slate-700 leading-relaxed font-normal">
              {bio}
            </p>
          </div>

          {/* Published Articles Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-extrabold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <BookOpen className="w-4 h-4 text-emerald-600" />
                <span>Articles by {authorName} ({authorArticles.length})</span>
              </span>
            </div>

            {authorArticles.length > 0 ? (
              <div className="space-y-2.5">
                {authorArticles.slice(0, 3).map((art) => (
                  <div
                    key={art.id}
                    onClick={() => handleArticleClick(art.slug)}
                    className="p-3 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3 cursor-pointer group transition-all shadow-2xs hover:border-emerald-300"
                  >
                    <img
                      src={art.coverImage}
                      alt={art.title}
                      className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0 group-hover:scale-105 transition-transform"
                    />
                    <div className="min-w-0 flex-1">
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (art.categoryId) setSelectedCategory(art.categoryId);
                          navigate('blog');
                          closeAuthorModal();
                        }}
                        className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700 hover:underline cursor-pointer"
                        title={`Filter by ${art.categoryName}`}
                      >
                        {art.categoryName}
                      </span>
                      {(() => {
                        const blogPrefix = (settings?.pageSlugs?.articles || settings?.pageSlugs?.blogSlug || 'blog').replace(/^\/+|\/+$/g, '');
                        const articleUrl = `/${blogPrefix}/${art.slug || art.id}`;
                        return (
                          <a
                            href={articleUrl}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-bold text-slate-900 group-hover:text-emerald-700 line-clamp-1 transition-colors block hover:underline"
                            title="Open article"
                          >
                            {art.title}
                          </a>
                        );
                      })()}
                      <span className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        {new Date(art.createdAt).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-slate-50 rounded-xl text-center text-xs text-slate-500">
                No other publications available from this author yet.
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={closeAuthorModal}
            className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 bg-slate-100 rounded-xl transition-colors cursor-pointer"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSeeAllArticles}
            className="px-4 py-2 text-xs font-bold text-white rounded-xl shadow-md hover:opacity-95 transition-all flex items-center gap-1.5 cursor-pointer"
            style={{ backgroundColor: primaryColor }}
          >
            <span>All Articles by {authorName}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
